//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { CATALOG_PRODUCT_IMAGE_SIZE } from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodySecondaryText, BodyText } from '@components/atoms/text/Text';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined } from '@utils/filterPredicates';

interface CatalogProductInfoProps {
  product: SamplingCatalogProduct;
  onSelectProduct: (product: SamplingCatalogProduct) => void;
}

export const CatalogProductInfo = ({
  onSelectProduct,
  product,
}: CatalogProductInfoProps) => {
  const upcs =
    isDefined(product.upcs) && product.upcs.length > 0
      ? product.upcs.join(', ')
      : DEFAULT_EMPTY_VALUE;
  const eans =
    isDefined(product.eans) && product.eans.length > 0
      ? product.eans.join(', ')
      : DEFAULT_EMPTY_VALUE;

  return (
    <CatalogProductInfoContainer>
      <PrimaryLinkButton
        onClick={() => onSelectProduct(product)}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_CATALOG_PRODUCT_SELECT_BUTTON
        }
      >
        Select this product
      </PrimaryLinkButton>
      {isDefined(product.name) && <BodyText>{product.name}</BodyText>}
      <ProductInfoLayout>
        <StyledImageWithErrorHandling
          alt={product.name ?? 'product image'}
          src={product.imageUrl}
        />
        <ProductDetailsTable>
          <DetailRow>
            <DetailLabel>ID:</DetailLabel>
            <BodySecondaryText>
              {product.id ?? DEFAULT_EMPTY_VALUE}
            </BodySecondaryText>
          </DetailRow>
          <DetailRow>
            <DetailLabel>UPC:</DetailLabel>
            <BodySecondaryText>{upcs}</BodySecondaryText>
          </DetailRow>
          <DetailRow>
            <DetailLabel>EAN:</DetailLabel>
            <BodySecondaryText>{eans}</BodySecondaryText>
          </DetailRow>
        </ProductDetailsTable>
      </ProductInfoLayout>
    </CatalogProductInfoContainer>
  );
};

const CatalogProductInfoContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacings[2]};
  background-color: ${theme.colors.white};
  box-shadow: ${theme.boxShadows[1]};
  padding: ${theme.spacings[3]};
`,
);

const ProductInfoLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const StyledImageWithErrorHandling = styled(ImageWithErrorHandling)(
  ({ theme }) => css`
    ${CATALOG_PRODUCT_IMAGE_SIZE({ theme })}
    object-fit: contain;
    background-color: ${theme.colors.gray0};
  `,
);

const ProductDetailsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const DetailRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const DetailLabel = styled(BodySecondaryText)`
  width: ${({ theme }) => theme.spacings[6]};
  text-align: right;
`;
